import React, { useEffect, useState } from 'react';
import { useTheme, DefaultTheme } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Brain,
  Settings,
  Plug,
  ChevronLeft,
  ChevronRight,
  Bot,
  FileText,
  Aperture,
  ChevronDown
} from 'lucide-react';
import styled from 'styled-components';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useSubscriptionStatus } from '../../hooks/useSubscriptionStatus';
import { subscriptionApi } from '../../services/api';

type MediaQueryListLegacy = {
  addListener?: (listener: (e: MediaQueryListEvent) => void) => void;
  removeListener?: (listener: (e: MediaQueryListEvent) => void) => void;
};

const SidebarContainer = styled(motion.aside)<{ $collapsed: boolean; $isMobile?: boolean }>`
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  right: auto;
  width: ${props => (props.$isMobile ? (props.$collapsed ? '0px' : '280px') : (props.$collapsed ? '72px' : '280px'))};
  background: ${props => props.theme.colors.backgroundSecondary};
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border-right: 1px solid ${props => props.theme.colors.border}80;
  z-index: 900;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: visible;
  transform: ${props => props.$isMobile && props.$collapsed ? 'translateX(-100%)' : 'translateX(0)'};
  display: flex;
  flex-direction: column;
  box-shadow: 0 0 40px rgba(0, 0, 0, 0.1);

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    box-shadow: ${props => !props.$collapsed ? '0 0 60px rgba(0, 0, 0, 0.3)' : 'none'};
  }
`;

const SidebarHeader = styled.div`
  padding: ${props => props.theme.spacing[5]};
  border-bottom: 1px solid ${props => props.theme.colors.border}40;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  position: relative;
  min-height: 72px;
  flex-shrink: 0;
  background: linear-gradient(180deg, ${props => props.theme.colors.backgroundTertiary}30 0%, transparent 100%);
`;

const ToggleButton = styled.button`
  background: ${props => props.theme.colors.backgroundTertiary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.lg};
  padding: ${props => props.theme.spacing[2]};
  color: ${props => props.theme.colors.textSecondary};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
    border-color: ${props => props.theme.colors.primary};
    transform: scale(1.05);
    box-shadow: 0 4px 12px ${props => props.theme.colors.primary}40;
  }
  
  &:active { transform: scale(0.95); }
`;

const Navigation = styled.nav`
  padding: ${props => props.theme.spacing[2]} 0;
  padding-bottom: ${props => props.theme.spacing[6]};
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: calc(100vh - 100px);

  /* Estilos de scrollbar personalizados */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.border};
    border-radius: 3px;

    &:hover {
      background: ${props => props.theme.colors.primary}60;
    }
  }
`;

const NavItem = styled(motion.div)<{ $active: boolean; $collapsed: boolean; $isSubItem?: boolean; $isSubSubItem?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${props => props.$collapsed ? 'center' : 'flex-start'};
  gap: ${props => props.$collapsed ? '0' : props.theme.spacing[3]};
  min-height: ${props => props.$isSubSubItem ? '36px' : '48px'};
  padding: ${props => props.$collapsed ? `${props.theme.spacing[3]}` : `${props.theme.spacing[3]} ${props.theme.spacing[4]}`};
  margin: ${props => props.$collapsed ? `${props.theme.spacing[1]} auto` : `${props.theme.spacing[1]} ${props.theme.spacing[3]}`};
  border-radius: ${props => props.theme.borders.radius.lg};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  width: ${props => props.$collapsed ? '48px' : 'calc(100% - ' + props.theme.spacing[6] + ')'};
  height: ${props => props.$collapsed ? '48px' : 'auto'};

  background: ${props => props.$active ? `linear-gradient(135deg, ${props.theme.colors.primary}15 0%, ${props.theme.colors.primary}08 100%)` : 'transparent'};
  color: ${props => props.$active ? props.theme.colors.primary : props.theme.colors.textSecondary};
  border: 1px solid ${props => props.$active ? `${props.theme.colors.primary}40` : 'transparent'};
  box-shadow: ${props => props.$active ? `0 2px 8px ${props.theme.colors.primary}10` : 'none'};

  ${props => props.$isSubItem && !props.$isSubSubItem && !props.$collapsed && `
    min-height: 40px;
    margin-left: 0;
    width: 100%;
    font-size: 13px;
    padding-left: ${props.theme.spacing[3]};

    &::before {
      content: '';
      position: absolute;
      left: -${props.theme.spacing[4]};
      top: 50%;
      width: ${props.theme.spacing[3]};
      height: 1px;
      background: ${props.$active ? props.theme.colors.primary : props.theme.colors.border};
      opacity: ${props.$active ? 1 : 0.4};
      transition: all 0.3s ease;
    }

    &:hover::before {
      opacity: 1;
      width: ${props.theme.spacing[4]};
      background: ${props.theme.colors.primary};
    }
  `}

  ${props => props.$isSubSubItem && !props.$collapsed && `
    min-height: 32px;
    margin-left: 0;
    width: 100%;
    font-size: 12px;
    padding: ${props.theme.spacing[2]} ${props.theme.spacing[3]};
    padding-left: ${props.theme.spacing[6]};

    &::before {
      content: '';
      position: absolute;
      left: ${props.theme.spacing[2]};
      top: 50%;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: ${props.$active ? props.theme.colors.primary : props.theme.colors.border};
      opacity: ${props.$active ? 1 : 0.5};
      transition: all 0.3s ease;
    }

    &:hover::before {
      opacity: 1;
      transform: scale(1.3);
      background: ${props.theme.colors.primary};
    }

    .nav-icon {
      width: 16px;
      height: 16px;
    }

    .nav-text {
      font-size: 12px;
    }
  `}

  &:hover {
    background: ${props => props.$active ? `linear-gradient(135deg, ${props.theme.colors.primary}20 0%, ${props.theme.colors.primary}10 100%)` : props.theme.colors.backgroundTertiary};
    color: ${props => props.$active ? props.theme.colors.primary : props.theme.colors.textPrimary};
    border-color: ${props => props.$active ? `${props.theme.colors.primary}60` : props.theme.colors.borderLight};
    ${props => props.$collapsed ? `
      transform: scale(1.08);
    ` : `
      transform: translateX(4px);
    `}
  }

  .nav-icon {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    ${props => props.$collapsed && `
      margin: 0 !important;
    `}
  }

  .nav-text {
    font-weight: ${props => props.$active ? props.theme.typography.fontWeight.semibold : props.theme.typography.fontWeight.medium};
    font-size: 14px;
    opacity: ${props => props.$collapsed ? 0 : 1};
    transition: opacity 0.3s;
    white-space: nowrap;
    ${props => props.$collapsed && `
      position: absolute;
      width: 0;
      height: 0;
      overflow: hidden;
    `}
  }

  .submenu-arrow {
    margin-left: auto;
    transition: transform 0.3s;
    ${props => props.$collapsed && `
      display: none;
    `}
  }
`;

const SubMenuContainer = styled(motion.div)`
  overflow: hidden;
  margin-left: ${props => props.theme.spacing[3]};
  padding-left: ${props => props.theme.spacing[4]};
  border-left: 2px solid ${props => props.theme.colors.border};
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: -2px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: linear-gradient(
      to bottom,
      ${props => props.theme.colors.primary}80,
      ${props => props.theme.colors.primary}20,
      transparent
    );
    opacity: 0.6;
  }
`;

const NavSection = styled.div`
  margin-bottom: ${props => props.theme.spacing[4]};
`;

const SectionDivider = styled.div<{ $collapsed: boolean }>`
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    ${props => props.theme.colors.border} 10%,
    ${props => props.theme.colors.border} 50%,
    ${props => props.theme.colors.border} 90%,
    transparent 100%
  );
  margin: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[4]};
  opacity: ${props => props.$collapsed ? 0 : 0.8};
  transition: opacity 0.3s;
`;

const SectionTitle = styled.div<{ $collapsed: boolean }>`
  padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[4]};
  font-size: 11px;
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.textTertiary};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  opacity: ${props => props.$collapsed ? 0 : 0.7};
  transition: opacity 0.3s;
`;

const MobileBackdrop = styled(motion.div)<{ $visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 899; // Below sidebar
  opacity: ${props => props.$visible ? 1 : 0};
  visibility: ${props => props.$visible ? 'visible' : 'hidden'};
  transition: all 0.3s;

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    display: none;
  }
`;

interface NavItemType {
  path?: string;
  icon?: React.ElementType;
  iconImage?: string;
  label: string;
  subItems?: NavItemType[];
}

const navigationItems: { section: string; items: NavItemType[] }[] = [
    {
        section: 'Principal',
        items: [
            { path: '/home', iconImage: '/icons/dashboard_icon.svg', label: 'Dashboard' },
            { path: '/reports', iconImage: '/icons/reports_icon.svg', label: 'Relatórios' },
        ],
    },
    {
        section: 'Gestão',
        items: [
            { path: '/leads', iconImage: '/icons/leads_icon.svg', label: 'Leads' },
            { path: '/sales', iconImage: '/icons/sales_icon.svg', label: 'Vendas' },
            { path: '/braip', iconImage: '/icons/braip_icon.webp', label: 'Braip' },
        ],
    },
    {
        section: 'Bots',
        items: [
            {
                label: 'WhatsApp',
                iconImage: '/icons/whatsapp_icon.svg',
                path: '/whatsapp',
                subItems: [
                    { path: '/bots-automations/integra', icon: Plug, label: 'Integração' },
                    { path: '/bots-automations/neural-core', icon: Brain, label: 'Núcleo Neural' },
                    { path: '/bots-automations/bot-settings', icon: Settings, label: 'Configurações do Bot' },
                    { path: '/bots-automations/media', icon: Aperture, label: 'Mídias' },
                    { path: '/bots-automations/logs', icon: FileText, label: 'Logs & Monitoramento' },
                ],
            },
            { path: '/gmail', iconImage: '/icons/gmail_icon.webp', label: 'Gmail' },
        ],
    },
    {
        section: 'Conta',
        items: [
            { path: '/profile', iconImage: '/icons/profile_icon.svg', label: 'Perfil' },
        ],
    },
];

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggle }) => {
  const { user } = useCurrentUser();
  const status = useSubscriptionStatus(user);
  const [removing, setRemoving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // Find which main menu is active based on the current path
    const activeMainItem = navigationItems
      .flatMap(s => s.items)
      .find(item => item.subItems?.some(sub => location.pathname.startsWith(sub.path!)));
    
    if (activeMainItem) {
      setOpenSubMenu(activeMainItem.label);
    }
  }, [location.pathname]);


  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile && !collapsed) onToggle();
  };

  const handleSubMenuToggle = (label: string) => {
    setOpenSubMenu(openSubMenu === label ? null : label);
  };

  const handleBackdropClick = () => {
    if (isMobile && !collapsed) onToggle();
  };

  const theme = useTheme() as DefaultTheme | undefined;
  const isLightTheme = theme?.isLight === true;

  // caminho dinâmico da logo
  // Logo dark (escuro) para tema light (fundo claro)
  // Logo light (claro) para tema dark (fundo escuro)
  const logoSrc = process.env.PUBLIC_URL
    ? process.env.PUBLIC_URL + (isLightTheme ? '/icons/loopra_logo_dark.webp' : '/icons/loopra_logo_light.webp')
    : (isLightTheme ? '/icons/loopra_logo_dark.webp' : '/icons/loopra_logo_light.webp');

  // pré-carrega as duas versões para evitar flicker/caching ao trocar tema
  useEffect(() => {
    const light = process.env.PUBLIC_URL ? process.env.PUBLIC_URL + '/icons/loopra_logo_light.webp' : '/icons/loopra_logo_light.webp';
    const dark = process.env.PUBLIC_URL ? process.env.PUBLIC_URL + '/icons/loopra_logo_dark.webp' : '/icons/loopra_logo_dark.webp';
    const imgs: HTMLImageElement[] = [new globalThis.Image(), new globalThis.Image()];
    imgs[0].src = light;
    imgs[1].src = dark;
    return () => { imgs.forEach(i => { i.src = ''; }); };
  }, []);

  return (
    <>
      <MobileBackdrop $visible={isMobile && !collapsed} onClick={handleBackdropClick} />
      <SidebarContainer $collapsed={collapsed} $isMobile={isMobile}>
      <SidebarHeader>
        {/* Sempre renderiza o logo e anima a opacidade para permitir fade-out suave ao colapsar */}
        <motion.img
          key={logoSrc}
          src={logoSrc}
          alt="Loopra Logo"
          // anima para 0 quando collapsed === true, ou para 1 quando aberto
          initial={{ opacity: collapsed ? 0 : 0 }}
          animate={{ opacity: collapsed ? 0 : 1 }}
          transition={{ duration: 0.15, ease: [0.4, 0, 0.2, 1] }}
          style={{
            position: 'absolute',
            left: '44%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: 128,
            height: 72,
            objectFit: 'contain',
            display: 'block',
            pointerEvents: 'none',
            filter: 'drop-shadow(0 2px 16px #0006)'
          }}
        />
        <ToggleButton onClick={onToggle} style={{ marginLeft: 'auto', zIndex: 1 }}>
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </ToggleButton>
      </SidebarHeader>

        <Navigation>
          {navigationItems.map((section, sectionIndex) => (
            <React.Fragment key={sectionIndex}>
              {sectionIndex > 0 && <SectionDivider $collapsed={collapsed} />}
              <NavSection>
                {!collapsed && <SectionTitle $collapsed={collapsed}>{section.section}</SectionTitle>}
                {section.items.map((item, itemIndex) => {
                const Icon = item.icon;
                const isSubMenuActive = item.subItems?.some(sub => location.pathname.startsWith(sub.path!)) ?? false;
                const isActive = !item.subItems && location.pathname.startsWith(item.path!);

                if (item.subItems) {
                  return (
                    <div key={itemIndex}>
                      <NavItem
                        $active={isSubMenuActive}
                        $collapsed={collapsed}
                        onClick={() => handleSubMenuToggle(item.label)}
                      >
                        {item.iconImage ? (
                          <img src={item.iconImage} alt={item.label} className="nav-icon" style={{ objectFit: 'contain' }} />
                        ) : Icon ? (
                          <Icon className="nav-icon" />
                        ) : null}
                        <span className="nav-text">{item.label}</span>
                        {!collapsed && (
                          <ChevronDown
                            className="submenu-arrow"
                            size={16}
                            style={{ transform: openSubMenu === item.label ? 'rotate(180deg)' : 'rotate(0deg)' }}
                          />
                        )}
                      </NavItem>
                      <AnimatePresence>
                        {!collapsed && openSubMenu === item.label && (
                          <SubMenuContainer
                            initial={{ height: 0 }}
                            animate={{ height: 'auto' }}
                            exit={{ height: 0 }}
                          >
                            {item.subItems.map((subItem, subIndex) => {
                              const SubIcon = subItem.icon;
                              const hasSubSubItems = subItem.subItems && subItem.subItems.length > 0;
                              const isSubSubMenuActive = hasSubSubItems && subItem.subItems.some(subSub => location.pathname.startsWith(subSub.path!));
                              const isSubActive = !hasSubSubItems && subItem.path && location.pathname.startsWith(subItem.path);

                              if (hasSubSubItems) {
                                return (
                                  <div key={subIndex}>
                                    <NavItem
                                      $active={isSubSubMenuActive}
                                      $collapsed={collapsed}
                                      $isSubItem
                                      onClick={() => handleSubMenuToggle(subItem.label)}
                                    >
                                      {subItem.iconImage ? (
                                        <img src={subItem.iconImage} alt={subItem.label} className="nav-icon" style={{ objectFit: 'contain' }} />
                                      ) : SubIcon ? (
                                        <SubIcon className="nav-icon" size={18} />
                                      ) : null}
                                      <span className="nav-text">{subItem.label}</span>
                                      <ChevronDown
                                        className="submenu-arrow"
                                        size={14}
                                        style={{ transform: openSubMenu === subItem.label ? 'rotate(180deg)' : 'rotate(0deg)' }}
                                      />
                                    </NavItem>
                                    <AnimatePresence>
                                      {openSubMenu === subItem.label && (
                                        <SubMenuContainer
                                          initial={{ height: 0 }}
                                          animate={{ height: 'auto' }}
                                          exit={{ height: 0 }}
                                        >
                                          {subItem.subItems.map((subSubItem, subSubIndex) => {
                                            const SubSubIcon = subSubItem.icon;
                                            const isSubSubActive = subSubItem.path && location.pathname.startsWith(subSubItem.path);
                                            return (
                                              <NavItem
                                                key={subSubIndex}
                                                $active={isSubSubActive}
                                                $collapsed={collapsed}
                                                $isSubItem
                                                $isSubSubItem
                                                onClick={() => handleNavigation(subSubItem.path!)}
                                              >
                                                {subSubItem.iconImage ? (
                                                  <img src={subSubItem.iconImage} alt={subSubItem.label} className="nav-icon" style={{ objectFit: 'contain' }} />
                                                ) : SubSubIcon ? (
                                                  <SubSubIcon className="nav-icon" size={16} />
                                                ) : null}
                                                <span className="nav-text">{subSubItem.label}</span>
                                              </NavItem>
                                            );
                                          })}
                                        </SubMenuContainer>
                                      )}
                                    </AnimatePresence>
                                  </div>
                                );
                              }

                              return (
                                <NavItem
                                  key={subIndex}
                                  $active={isSubActive}
                                  $collapsed={collapsed}
                                  $isSubItem
                                  onClick={() => subItem.path && handleNavigation(subItem.path)}
                                >
                                  {subItem.iconImage ? (
                                    <img src={subItem.iconImage} alt={subItem.label} className="nav-icon" style={{ objectFit: 'contain' }} />
                                  ) : SubIcon ? (
                                    <SubIcon className="nav-icon" size={18} />
                                  ) : null}
                                  <span className="nav-text">{subItem.label}</span>
                                </NavItem>
                              );
                            })}
                          </SubMenuContainer>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }

                return (
                  <NavItem
                    key={itemIndex}
                    $active={isActive}
                    $collapsed={collapsed}
                    onClick={() => handleNavigation(item.path!)}
                  >
                    {item.iconImage ? (
                      <img src={item.iconImage} alt={item.label} className="nav-icon" style={{ objectFit: 'contain' }} />
                    ) : Icon ? (
                      <Icon className="nav-icon" />
                    ) : null}
                    <span className="nav-text">{item.label}</span>
                  </NavItem>
                );
              })}
              </NavSection>
            </React.Fragment>
          ))}
        </Navigation>
      </SidebarContainer>
    </>
  );
};

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default Sidebar;
